import { useState, useRef } from 'react';
import { Loader2, Check, AlertCircle, Upload, X, Star, FileText } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/** Renders any form schema and handles submission.
 *  Handles all field types: text, email, textarea, number, phone, date,
 *  select, radio, checkbox, image, heading, paragraph, file, rating */
export default function PublicFormRenderer({ form, onSubmitted, embedded = false }) {
  const [values, setValues] = useState({});
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [uploadingField, setUploadingField] = useState(null);

  if (!form) return null;
  const setField = (id, v) => setValues((p) => ({ ...p, [id]: v }));
  const accent = form.accent_color || '#A78BFA';

  // ── File upload handler ──
  const handleFile = async (fieldId, e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';
    setUploadingField(fieldId);
    const uploaded = Array.isArray(values[fieldId]) ? [...values[fieldId]] : [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API}/forms/${form.slug}/upload`, { method: 'POST', body: fd });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          uploaded.push({ url: data.url, filename: data.filename, size: data.size });
        } else {
          setStatus('error');
          setMessage(data.detail || 'File upload failed');
        }
      } catch {
        setStatus('error');
        setMessage('File upload failed');
      }
    }
    setField(fieldId, uploaded);
    setUploadingField(null);
  };

  const removeFile = (fieldId, idx) => {
    const arr = [...(values[fieldId] || [])];
    arr.splice(idx, 1);
    setField(fieldId, arr);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;

    const DISPLAY_ONLY = new Set(['image', 'heading', 'paragraph']);
    for (const f of form.fields || []) {
      if (DISPLAY_ONLY.has(f.type)) continue;
      if (f.required) {
        const v = values[f.id];
        const isEmpty = (v === undefined || v === null || (typeof v === 'string' && !v.trim()) || (Array.isArray(v) && v.length === 0));
        if (isEmpty) {
          setStatus('error');
          setMessage(`Please fill in: ${f.label}`);
          return;
        }
      }
    }

    setStatus('submitting');
    setMessage('');
    try {
      const res = await fetch(`${API}/forms/${form.slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: values }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      setStatus('success');
      setMessage(data.message || form.success_message || 'Thanks!');
      setValues({});
      onSubmitted?.(data);
    } catch (err) {
      setStatus('error');
      setMessage(err.message || 'Submission failed');
    }
  };

  const inputCls = 'w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/15 text-white text-sm placeholder-[#64748B] focus:outline-none transition';
  const focusBorder = { ['--tw-ring-color']: accent };

  if (status === 'success' && !embedded) {
    return (
      <div className="text-center py-12" data-testid="form-success">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-green-500/15 text-green-400 mb-4">
          <Check size={28} />
        </div>
        <h3 className="font-syne text-2xl font-bold text-white mb-2">{message}</h3>
        <button onClick={() => { setStatus('idle'); setMessage(''); }} className="mt-4 text-sm hover:text-white" style={{ color: accent }}>Submit another response</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid={`form-render-${form.slug}`} style={focusBorder}>
      {form.fields?.map((f) => {
        // ── Display-only blocks ──
        if (f.type === 'image') {
          return f.image_url ? (
            <div key={f.id} className="rounded-xl overflow-hidden border border-white/10 bg-black">
              <img src={f.image_url.startsWith('http') ? f.image_url : `${process.env.REACT_APP_BACKEND_URL}${f.image_url}`} alt={f.label || 'Form image'} className="w-full h-auto block" />
              {f.label && <p className="px-3 py-2 text-xs text-zinc-300 italic">{f.label}</p>}
            </div>
          ) : null;
        }
        if (f.type === 'heading') {
          return <h3 key={f.id} className="font-syne text-2xl font-black text-white mt-2 mb-1 break-words">{f.label}</h3>;
        }
        if (f.type === 'paragraph') {
          return <p key={f.id} className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{f.label}</p>;
        }

        // ── Input fields ──
        return (
          <div key={f.id}>
            <label className="block text-white text-sm font-semibold mb-1.5">
              {f.label}{f.required && <span className="text-red-400 ml-1">*</span>}
            </label>

            {f.type === 'textarea' ? (
              <textarea rows={5} value={values[f.id] || ''} onChange={(e) => setField(f.id, e.target.value)} placeholder={f.placeholder || ''} className={inputCls} data-testid={`field-${f.id}`} />
            ) : f.type === 'select' ? (
              <select value={values[f.id] || ''} onChange={(e) => setField(f.id, e.target.value)} className={inputCls} data-testid={`field-${f.id}`}>
                <option value="" className="bg-[#13153A]">{f.placeholder || 'Select…'}</option>
                {(f.options || []).map((opt) => <option key={opt} value={opt} className="bg-[#13153A]">{opt}</option>)}
              </select>
            ) : f.type === 'radio' ? (
              <div className="space-y-2">
                {(f.options || []).map((opt) => (
                  <label key={opt} className="flex items-center gap-2 cursor-pointer text-zinc-200 text-sm">
                    <input type="radio" name={f.id} value={opt} checked={values[f.id] === opt} onChange={(e) => setField(f.id, e.target.value)} />
                    {opt}
                  </label>
                ))}
              </div>
            ) : f.type === 'checkbox' ? (
              <div className="space-y-2">
                {(f.options || []).map((opt) => {
                  const arr = values[f.id] || [];
                  const checked = arr.includes(opt);
                  return (
                    <label key={opt} className="flex items-center gap-2 cursor-pointer text-zinc-200 text-sm">
                      <input type="checkbox" checked={checked} onChange={(e) => {
                        const next = new Set(arr);
                        if (e.target.checked) next.add(opt); else next.delete(opt);
                        setField(f.id, Array.from(next));
                      }} />
                      {opt}
                    </label>
                  );
                })}
              </div>
            ) : f.type === 'rating' ? (
              <div className="flex items-center gap-1.5" data-testid={`field-${f.id}`}>
                {[...Array(f.max_rating || 5)].map((_, i) => {
                  const n = i + 1;
                  const filled = (values[f.id] || 0) >= n;
                  return (
                    <button type="button" key={n} onClick={() => setField(f.id, n)} className="p-0.5">
                      <Star size={28} className={`transition ${filled ? 'fill-yellow-400 text-yellow-400 drop-shadow-[0_0_6px_rgba(234,179,8,0.6)]' : 'text-zinc-500 hover:text-yellow-400'}`} />
                    </button>
                  );
                })}
                {values[f.id] ? <span className="ml-2 text-sm text-zinc-300">{values[f.id]} / {f.max_rating || 5}</span> : null}
              </div>
            ) : f.type === 'file' ? (
              <FileFieldInput
                fieldId={f.id}
                values={values[f.id] || []}
                onUpload={(e) => handleFile(f.id, e)}
                onRemove={(idx) => removeFile(f.id, idx)}
                uploading={uploadingField === f.id}
                maxFiles={f.max_files || 3}
              />
            ) : (
              <input type={f.type === 'phone' ? 'tel' : f.type === 'date' ? 'date' : f.type} value={values[f.id] || ''} onChange={(e) => setField(f.id, e.target.value)} placeholder={f.placeholder || ''} className={inputCls} data-testid={`field-${f.id}`} />
            )}
            {f.help && <p className="text-xs text-[#9CA3AF] mt-1">{f.help}</p>}
          </div>
        );
      })}

      {message && status === 'error' && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="form-error">
          <AlertCircle size={14} /> {message}
        </div>
      )}
      {message && status === 'success' && embedded && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/30 text-green-300 text-sm">
          <Check size={14} /> {message}
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full px-4 py-3 rounded-xl text-black font-bold disabled:opacity-60 transition flex items-center justify-center gap-2"
        style={{ backgroundColor: accent }}
        data-testid="form-submit"
      >
        {status === 'submitting' && <Loader2 size={16} className="animate-spin" />}
        {status === 'submitting' ? 'Sending…' : (form.submit_button_label || 'Submit')}
      </button>
    </form>
  );
}

function FileFieldInput({ fieldId, values, onUpload, onRemove, uploading, maxFiles }) {
  const inputRef = useRef(null);
  const atLimit = values.length >= maxFiles;
  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" multiple onChange={onUpload} className="hidden" data-testid={`field-${fieldId}`} />
      {!atLimit && (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="w-full px-4 py-3 rounded-xl bg-white/5 border-2 border-dashed border-white/15 hover:border-[#A78BFA]/60 hover:bg-white/10 text-zinc-200 text-sm flex items-center justify-center gap-2 transition disabled:opacity-60">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? 'Uploading…' : `Choose file${maxFiles > 1 ? 's' : ''} ${values.length > 0 ? `(${values.length}/${maxFiles})` : ''}`}
        </button>
      )}
      {values.length > 0 && (
        <div className="space-y-1.5">
          {values.map((v, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs" data-testid={`file-${fieldId}-${i}`}>
              <FileText size={13} className="text-[#A78BFA] flex-shrink-0" />
              <span className="text-white truncate flex-1">{v.filename}</span>
              <span className="text-[#9CA3AF] text-[10px]">{Math.round((v.size || 0) / 1024)} KB</span>
              <button type="button" onClick={() => onRemove(i)} className="text-red-400 hover:text-red-300 flex-shrink-0"><X size={13} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
